import { Box, Container } from '@mui/material';
import { useState } from 'react';
import { Outlet, useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getUserDetails } from '../../../store/ui/selector';
import { submitDocuments } from '../../../services/plaid.service';
import { validateSingleDocument } from '../../../services/document-validation.service';
import { useTranslation } from 'react-i18next';

// Toggle: Upload Precheck Mode
//  - 'off'   : no validation at add time
//  - 'warn'  : show warnings for scanned/print-to-PDF, still accept
//  - 'block' : hard reject invalid files
const UPLOAD_PRECHECK_MODE: 'off' | 'warn' | 'block' = 'warn';

export type DocType = 'Paystub' | 'Bank Statement' | 'Offer Letter';
export type UploadSubRoute = '' | 'select' | 'review' | 'fallback';

export interface UploadContext {
  files: File[];
  addFiles: (fls: FileList | File[] | null, navigateOnSuccess?: boolean) => Promise<void>;
  removeFile: (idx: number) => void;

  docType: DocType | '';
  setDocType: (v: DocType | '') => void;
  frequency: string;
  setFrequency: (v: string) => void;
  employerEmail: string;
  setEmployerEmail: (v: string) => void;
  documentRequiredCount: number;
  setDocumentRequiredCount: (n: number) => void;

  isValidating: boolean;
  validationError: string;
  setValidationError: (v: string) => void;
  showValidationError: boolean;
  setShowValidationError: (v: boolean) => void;
  validationWarnings: { fileName: string; message: string }[];

  fallbackTriggered: boolean;
  lastFailedDocType: DocType | null;
  lockedDocType: DocType | null;
  pickBankStmtFallback: () => void;
  pickPaystubFallback: () => void;
  pickRetryUpload: () => void;
  continueAnyway: () => Promise<void>;

  submit: () => Promise<void>;
  isSubmitting: boolean;
  submitError: string;
  setSubmitError: (v: string) => void;
  goTo: (sub: UploadSubRoute) => void;
  goToTypes: () => void;
}

export default function DocumentUpload() {
  const { propertyCode } = useParams();
  const { search } = useLocation();
  const navigate = useNavigate();
  const userDetails = useSelector(getUserDetails);
  const { t } = useTranslation();

  const [files, setFiles] = useState<File[]>([]);
  const [docType, setDocType] = useState<DocType | ''>('');
  const [frequency, setFrequency] = useState<string>('');
  // Optional employer/HR contact, captured for offer letters & paystubs so a PM
  // can fall back to an employer verification if the document gets flagged.
  const [employerEmail, setEmployerEmail] = useState<string>('');
  const [documentRequiredCount, setDocumentRequiredCount] = useState(0);
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [showValidationError, setShowValidationError] = useState(false);
  const [validationWarnings, setValidationWarnings] = useState<{ fileName: string; message: string }[]>([]);
  const [fallbackTriggered, setFallbackTriggered] = useState(false);
  const [lastFailedDocType, setLastFailedDocType] = useState<DocType | null>(null);
  const [lockedDocType, setLockedDocType] = useState<DocType | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const basePath = `/${propertyCode}/verification/upload`;

  const goTo = (sub: UploadSubRoute) => {
    setSubmitError('');
    const path = sub ? `${basePath}/${sub}` : basePath;
    navigate(`${path}${search}`);
  };
  const goToTypes = () => navigate(`/${propertyCode}/verification/types${search}`);
  const goToConfirmation = () => navigate(`/${propertyCode}/verification/confirmation${search}`);

  const addFiles = async (fls: FileList | File[] | null, navigateOnSuccess = true) => {
    if (!fls) return;
    const accepted = Array.from(fls).filter(f => f.type === 'application/pdf');

    if (accepted.length === 0) {
      setValidationError(t('verification.type.document.invalidPdfError'));
      setShowValidationError(true);
      return;
    }

    setValidationError('');
    setShowValidationError(false);
    setIsValidating(UPLOAD_PRECHECK_MODE !== 'off');

    const newBatchNames = new Set(accepted.map(f => f.name));

    try {
      if (UPLOAD_PRECHECK_MODE === 'warn') {
        const newWarnings: { fileName: string; message: string }[] = [];
        let anyFailed = false;
        for (const file of accepted) {
          try {
            const validation: any = await validateSingleDocument(file);
            if (validation && validation.isValid === false) {
              anyFailed = true;
              const msg = validation.warningMessage || validation.errorMessage || t('verification.type.document.detectedIssues');
              newWarnings.push({ fileName: file.name, message: msg });
            }
          } catch {
            anyFailed = true;
          }
        }
        // Preserve warnings for files already in state; replace any entries
        // for the current batch with fresh results.
        setValidationWarnings(prev => [...prev.filter(w => !newBatchNames.has(w.fileName)), ...newWarnings]);
        if (anyFailed && (docType === 'Paystub' || docType === 'Bank Statement' || docType === 'Offer Letter')) {
          setFallbackTriggered(true);
          setLastFailedDocType(docType);
        }
      } else if (UPLOAD_PRECHECK_MODE === 'block') {
        for (const file of accepted) {
          const validation: any = await validateSingleDocument(file);
          if (!validation?.isValid) {
            setValidationError(validation?.errorMessage || t('verification.type.document.validationFailedError'));
            setShowValidationError(true);
            return;
          }
        }
      }

      const next = [...files, ...accepted];
      setFiles(next);
      if (next.length && navigateOnSuccess) goTo('review');
    } catch {
      setValidationError(t('verification.type.document.validationErrorGeneric'));
      setShowValidationError(true);
    } finally {
      setIsValidating(false);
    }
  };

  const removeFile = (idx: number) => {
    const removed = files[idx];
    const next = files.slice();
    next.splice(idx, 1);
    setFiles(next);
    setValidationError('');
    setShowValidationError(false);
    if (removed) {
      setValidationWarnings(prev => {
        const remaining = prev.filter(w => w.fileName !== removed.name);
        if (remaining.length === 0) {
          setFallbackTriggered(false);
          setLastFailedDocType(null);
        }
        return remaining;
      });
    }
  };

  const pickBankStmtFallback = () => {
    setLockedDocType('Bank Statement');
    setFiles([]);
    setDocType('Bank Statement');
    setFrequency('');
    setDocumentRequiredCount(2);
    setValidationWarnings([]);
    setValidationError('');
    setShowValidationError(false);
    setFallbackTriggered(false);
    setLastFailedDocType(null);
    goTo('select');
  };

  const pickPaystubFallback = () => {
    setLockedDocType('Paystub');
    setFiles([]);
    setDocType('Paystub');
    // Frequency drives the required count, so user has to pick it again.
    setFrequency('');
    setDocumentRequiredCount(0);
    setValidationWarnings([]);
    setValidationError('');
    setShowValidationError(false);
    setFallbackTriggered(false);
    setLastFailedDocType(null);
    goTo('select');
  };

  // Retry path: same docType, fresh files. Keeps frequency/required count and
  // the lockedDocType (so the locked label stays if applicable).
  const pickRetryUpload = () => {
    setFiles([]);
    setValidationWarnings([]);
    setValidationError('');
    setShowValidationError(false);
    setFallbackTriggered(false);
    setLastFailedDocType(null);
    goTo('select');
  };

  // Confirmation only once the API has the documents. This used to ignore the result and show
  // "received" even when the upload failed, so the PM never got them and nobody knew.
  const sendDocuments = async () => {
    setSubmitError('');
    const applicantId = userDetails?.applicant_id;
    const applicationId = userDetails?.application_id;
    if (!applicantId || !applicationId) {
      setSubmitError(t('verification.type.document.submitFailedError'));
      return;
    }
    setIsSubmitting(true);
    try {
      const result = await submitDocuments(files, applicantId, applicationId, employerEmail.trim() || undefined);
      if (result.status === 'success') {
        goToConfirmation();
      } else {
        // Files stay in state, so pressing the button again retries the same upload.
        setSubmitError(t('verification.type.document.submitFailedError'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const submit = async () => {
    if (fallbackTriggered) {
      goTo('fallback');
      return;
    }
    await sendDocuments();
  };

  // Bypass the fallback gate and submit whatever the user has uploaded, even
  // though validation flagged something. The user has been warned this will
  // probably stall their lease application.
  const continueAnyway = sendDocuments;

  const ctx: UploadContext = {
    files,
    addFiles,
    removeFile,
    docType,
    setDocType,
    frequency,
    setFrequency,
    employerEmail,
    setEmployerEmail,
    documentRequiredCount,
    setDocumentRequiredCount,
    isValidating,
    validationError,
    setValidationError,
    showValidationError,
    setShowValidationError,
    validationWarnings,
    fallbackTriggered,
    lastFailedDocType,
    lockedDocType,
    pickBankStmtFallback,
    pickPaystubFallback,
    pickRetryUpload,
    continueAnyway,
    submit,
    isSubmitting,
    submitError,
    setSubmitError,
    goTo,
    goToTypes,
  };

  return (
    <Container maxWidth="sm" sx={{ px: { xs: 2, md: 3 }, pb: 4 }}>
      <Box>
        <Outlet context={ctx} />
      </Box>
    </Container>
  );
}

export function useUpload() {
  return useOutletContext<UploadContext>();
}
